/*
 * Route Subscription Form Component
 * 
 * Enhanced form for creating route subscriptions with:
 * - Town origin selection
 * - Departure time selection  
 * - Interactive map-based location picker
 * - Distance-based notification settings
 * - Current location detection with fallback
 * 
 * FEATURES TO EDIT:
 * - Town list: Update BELIZE_TOWNS in utils/config.js
 * - Time options: Modify DEPARTURE_TIMES in utils/config.js  
 * - Distance options: Change notification distance ranges
 * - Map picker: Customize location selection behavior
 * 
 * LOCATION IN CODE: Search for "RouteSubscriptionForm" function
 */

import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { 
  API, 
  APP_CONSTANTS, 
  BELIZE_TOWNS, 
  getCurrentLocation 
} from '../../utils/config';
import LocationPicker from '../Map/LocationPicker';
import MapController from '../Map/MapController';

/**
 * Route Subscription Form Component
 * Interactive form with map-based location selection
 */
const RouteSubscriptionForm = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    origin_town: '',
    departure_time: '',
    pickup_location: null,
    notification_distance_miles: APP_CONSTANTS.DEFAULTS.SUBSCRIPTION.NOTIFICATION_DISTANCE
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mapCenter, setMapCenter] = useState(APP_CONSTANTS.MAP.BELIZE_CENTER);
  const [loadingLocation, setLoadingLocation] = useState(true);

  /**
   * Initialize form with current location
   */
  useEffect(() => {
    const initializeLocation = async () => {
      try {
        setLoadingLocation(true);
        const currentLocation = await getCurrentLocation();
        setFormData(prev => ({
          ...prev,
          pickup_location: currentLocation
        }));
        setMapCenter([currentLocation.lat, currentLocation.lng]);
      } catch (error) {
        // Default location already set in mapCenter
      } finally {
        setLoadingLocation(false);
      }
    };

    initializeLocation();
  }, []);

  /**
   * Handle form input changes
   */
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };

  /**
   * Handle map location selection
   */
  const handleLocationSelect = (latlng) => {
    setFormData(prev => ({
      ...prev,
      pickup_location: {
        lat: latlng.lat,
        lng: latlng.lng
      }
    }));
  };

   /**
 * StopSwipeBubble Component
 * Prevents swipe / pan events inside the map from bubbling up to the
 * global swipe-to-change-tab handler.
 */
const StopSwipeBubble = ({ children }) => {
  const haltStart = (e) => e.stopPropagation();
  return (
    <div
      className="w-full h-full" 
      onTouchStart={haltStart}
      onPointerDown={haltStart}
    >
      {children}
    </div>
  );
};

const StopSwipeBubbleForm = ({ children }) => {
  const haltStart = (e) => e.stopPropagation();
  return (
    <div
      // className="w-full h-full" 
      onTouchStart={haltStart}
      onPointerDown={haltStart}
    >
      {children}
    </div>
  );
};

  /**
   * Reset to current location and update map view
   */
  const resetToCurrentLocation = async () => {
    try {
      setLoadingLocation(true);
      const currentLocation = await getCurrentLocation();
      handleLocationSelect(currentLocation);
      setMapCenter([currentLocation.lat, currentLocation.lng]);
      
      // Force map to re-render with new center
      setTimeout(() => {
        const mapElement = document.querySelector('.leaflet-container');
        if (mapElement && mapElement._leaflet_map) {
          mapElement._leaflet_map.setView([currentLocation.lat, currentLocation.lng], 15);
        }
      }, 100);
    } catch (error) {
      alert('Unable to access your current location. Please select manually on the map.');
    } finally {
      setLoadingLocation(false);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate required fields
    if (!formData.origin_town) {
      setError('Please select an origin town');
      setLoading(false);
      return;
    }

    if (!formData.departure_time) {
      setError('Please select a departure time');
      setLoading(false);
      return;
    }

    if (!formData.pickup_location) {
      setError('Please select a pickup location on the map');
      setLoading(false);
      return;
    }

    try {
      // Submit route subscription
      await axios.post(`${API}/route-subscriptions`, formData);
      
      // Show success message
      alert('Route subscription created successfully! You will receive notifications when buses approach your pickup location.');
      
      // Call parent callbacks
      onSubmit();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create route subscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    // <StopSwipeBubbleForm>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg p-8 max-w-4xl w-full max-h-screen overflow-y-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Subscribe to Bus Route</h3>
        
        {/* Information Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-blue-800 mb-2">🔔 How Route Subscriptions Work</h4>
          <p className="text-sm text-blue-700">
            Get notified when buses from your selected town and departure time are within your specified 
            distance from your pickup location. Use the map below to set your exact pickup point.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Form Fields */}
            <div className="space-y-6">
              {/* Route Information Section */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Route Information</h4>
                
                {/* Origin Town Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    1. Town the bus originates from *
                  </label>
                  <select
                    name="origin_town"
                    value={formData.origin_town}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="">Select origin town...</option>
                    {BELIZE_TOWNS.map(town => (
                      <option key={town} value={town}>{town}</option>
                    ))}
                  </select>
                </div>

                {/* Departure Time Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    2. Time the bus leaves *
                  </label>
                  <select
                    name="departure_time"
                    value={formData.departure_time}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="">Select departure time...</option>
                    {APP_CONSTANTS.DEFAULTS.SUBSCRIPTION.DEPARTURE_TIMES.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>

                {/* Notification Distance */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    4. Notification distance from pickup location *
                  </label>
                  <select
                    name="notification_distance_miles"
                    value={formData.notification_distance_miles}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value={0.5}>0.5 miles (Very close)</option>
                    <option value={1.0}>1.0 mile (Close)</option>
                    <option value={2.0}>2.0 miles (Moderate distance)</option>
                    <option value={3.0}>3.0 miles (Far distance)</option>
                    <option value={5.0}>5.0 miles (Very far distance)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    You'll be notified when buses are within this distance of your pickup location
                  </p>
                </div>
              </div>

              {/* Location Controls */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Pickup Location Controls</h4>
                
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={resetToCurrentLocation}
                    disabled={loadingLocation}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {loadingLocation ? 'Getting Location...' : '📍 Use Current Location'}
                  </button>
                  
                  {formData.pickup_location && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                      <h5 className="font-medium text-green-800 mb-1">Selected Pickup Location:</h5>
                      <p className="text-sm text-green-700 font-mono">
                        {formData.pickup_location.lat.toFixed(6)}, {formData.pickup_location.lng.toFixed(6)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              {/* Form Buttons */}
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.pickup_location}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Creating Subscription...' : 'Subscribe to Route'}
                </button>
              </div>
            </div>

            {/* Right Column - Interactive Map */}
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-4">
                3. Select your pickup location on the map *
              </h4>
              
              {/* Map Instructions */}
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-700">
                  <strong>📍 Click anywhere on the map</strong> to set your pickup location. 
                  The purple marker shows where you'll receive notifications.
                </p>
              </div>

              {/* Interactive Map */}
              <div className="rounded-lg overflow-hidden shadow-lg border" style={{ height: '400px' }}>
                {/* <StopSwipeBubble>  */}
                <MapContainer 
                  center={mapCenter} 
                  zoom={13} 
                  style={{ height: '100%', width: '100%' }}
                  key={`${mapCenter[0]}-${mapCenter[1]}`} // Force re-render when center changes
                >
                  <MapController center={mapCenter} zoom={13} />
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  
                  <LocationPicker
                    position={formData.pickup_location}
                    onLocationSelect={handleLocationSelect}
                  />
                </MapContainer>
                {/* </StopSwipeBubble>  */}
              </div>

              {/* Map Legend */}
              <div className="mt-3 text-xs text-gray-600">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                    <span>Your pickup location</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                    <span>Click to place marker</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
    // </StopSwipeBubbleForm>
  );
};

export default RouteSubscriptionForm;